/* eslint-disable no-console */
import {
  faCircleInfo,
  faEye,
  faPaintbrush,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ImageLightbox,
  Loading,
  LoadingBlock,
} from '@orobix/orobix-fe-components'
import { AxiosError } from 'axios'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { toast } from 'react-toastify'

import { useDispatch, useStore } from '../../store/store'
import { AlertNotificationImageUpload } from '../../store/store.types'
import { showAlertNotification } from '../../store/storeActions'
import { getAnnotationTypeBy } from '../../types/annotation'
import { ObservationsResult } from '../../types/observation'
import { Sorting, SortingDirection } from '../../types/sorting'
import { get, post, update } from '../../utils/api'
import { handleApiErrors } from '../../utils/apiErrors'
import { overrideLabelStudioSettings } from '../../utils/labelStudioHelper'
import { getQueryString } from '../../utils/queryString'
import { serializeSorting } from '../../utils/sorting'
import Annotator from '../Annotator/Annotator'
import { ObservationsBrowserTop } from '../ObservationsBrowserTop/ObservationsBrowserTop'
import UploadObservations from '../UploadObservations/UploadObservations'
import { markUploadCompleted } from '../UploadObservations/useCreateObservation'
import { Divider } from './Helpers/Divider'
import { InfiniteListWrapper } from './Helpers/InfiniteListWrapper'
import { SwitchViewButtons } from './Helpers/SwitchViewButtons'
import useStyles from './ObservationsBrowser.styles'
import {
  isGridMode,
  isTabularMode,
  Observation,
  ObservationsBrowserMode,
  ObservationsBrowserProps,
} from './types'
import { useObservation } from './useObservation'
import {
  getObservationsQueryParams,
  ObservationsParams,
  useObservations,
} from './useObservations'
import { useObservationsDelete } from './useObservationsDelete'
import { useObservationsPrepend } from './useObservationsPrepend'
import { useObservationsUpdate } from './useObservationsUpdate'
import { getLightweightParams, getTagDecorator } from './utils'
import { getItemRenderer, getNextMode, getViewComponent } from './Views/factory'
import { MenuContextualItems, OnActionCallback } from './Views/types'

// Logging prefix for easy filtering in console
const LOG_PREFIX = '[ObservationsBrowser]'

/**
 * ## `<ObservationsBrowser />` component
 *
 * Customizable browser with a grid and a tabular view.
 *
 * Implements an observations list with its fetching logic, but allows to customize
 * views and actions for each item. Default implementation are provided.
 *
 * ### Features
 *
 * - Switchable grid and tabular views
 * - Fetching with infinite scroll
 * - Customizing top bar components (e.g. filters, selectors)
 * - Per item callbacks (e.g. onItemClick)
 * - Per item customizations (e.g. menuContextualItems, tagDecorator)
 * - Customizable item renderer (customize both grid and tabular items), i.e. low
 *   level API for item rendering
 *
 * ### Usage
 *
 * Basic component offers a default grid and tabular view and allows to annotate
 * observation on along with two extra actions (details viewer and delete).
 *
 * ```tsx
 * <ObservationsBrowser
 *   productType="segmentation"
 *   productId={3}
 *   mode="tabular"
 * />
 * ```
 *
 * You can customize item actions behavior by providing onClick callback, menu
 * items and tag decorator. Also the browser top bar can be customized with widgets.
 *
 * ```tsx
 * const MyComponent = () => {
 *   const handleItemClick = ({ observation }) => {
 *     console.log(observation);
 *   }
 *
 *   const menuContextualItems = ({ observation }) => [
 *     {
 *       label: t('Image details'),
 *       icon: <FontAwesomeIcon icon={faImage} />,
 *       type: 'default',
 *       hideLabel: true,
 *       onClick: () => console.log(observation),
 *     },
 *   ]
 *
 *   const tagDecorator = ({ observation }) => {
 *     return <p><FontAwesomeIcon icon={faTag} /> {observation.productClasses?.join()}</p>
 *   }
 *
 *   return (
 *     <ObservationsBrowser
 *       productType="segmentation"
 *       productId={3}
 *       mode="tabular"
 *       onItemClick={handleItemClick}
 *       menuContextualItems={menuContextualItems}
 *       tagDecorator={tagDecorator}
 *     />
 *   )
 * }
 * ```
 *
 * More complex scenarios instead can be achieved by providing custom item
 * renderer. In such cases you need to provide a low level implementation of grid
 * and tabular items.
 *
 * ```tsx
 * const MyComponent = () => {
 *   return (
 *     <ObservationsBrowser
 *       productType="segmentation"
 *       productId={3}
 *       mode="tabular"
 *       renderGridItem={({ observation }) => (
 *         <div>{observation.id}</div>
 *       )}
 *       renderTabularItem={({ observation }) => (
 *         <tr>
 *           <td>{observation.id}</td>
 *           <td>{observation.filename}</td>
 *         </tr>
 *       )}
 *     />
 *   )
 * }
 * ```
 *
 * ### Params
 *
 * @param productType - Type of product
 * @param productIt - Id of the product
 * @param widgets - An (eventually empty) component with widgets to be displayed in
 *                  the top bar
 * @param defaultFilters - Default filters applied to fetching mechanism
 * @param onItemClick - Callback called when an item is clicked (either a grid item
 *                      or a tabular row)
 * @parma menuContextualItems - Extra actions per each item
 * @param tagDecorator - Component used to render observation tags both in grid and
 *                       tabular view
 * @param renderGridItem - Custom component that renders a grid item
 * @param renderTabularItem - Custom component that renders a tabular item (i.e. a row)
 * @param mode - Initial mode of the browser (grid or tabular)
 * @param onModeChange - Callback called when the mode is changed
 */
export const ObservationsBrowser = ({
  productType,
  productId,
  trainingId,
  widgets,
  defaultFilters,
  onItemClick,
  menuContextualItems,
  tagDecorator,
  renderGridItem,
  renderTabularItem,
  mode = 'grid',
  onModeChange,
  setVisibleObservations,
  showUploadObservationsButton = true,
  uploadDisabled = false,
  imageData = false,
  dataset,
  viewingMode,
  order,
  direction,
  onObservationsCreated,
  updateSelectedObservations,
  useWindowScroll = true,
  scrollContainerId,
  initialPageSize,
  resetFiltersOnDatasetChange = false,
  subHeaderContent,
}: ObservationsBrowserProps) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const defaultLightweightParams = getLightweightParams(mode, imageData)

  const [params, setParams] = useState<ObservationsParams>({
    productId,
    ...defaultFilters,
    ...defaultLightweightParams,
    dataset,
    order,
    direction,
    limit: initialPageSize,
  })

  useEffect(() => {
    if (resetFiltersOnDatasetChange && dataset && dataset.length > 0) {
      // Reset filters when dataset is selected
      setParams((p) => ({
        ...p,
        dataset,
        annotationClasses: undefined,
        productClasses: undefined,
        availableForTesting: undefined,
        filename: undefined,
        datasetExclude: undefined,
      }))
    } else {
      setParams((p) => ({ ...p, dataset }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataset, resetFiltersOnDatasetChange])

  useEffect(() => {
    setParams((p) => ({ ...p, order }))
  }, [order])

  useEffect(() => {
    setParams((p) => ({ ...p, direction }))
  }, [direction])

  useEffect(() => {
    if (initialPageSize !== undefined) {
      setParams((p) => ({ ...p, limit: initialPageSize }))
    }
  }, [initialPageSize])

  const [currentObservationId, setCurrentObservationId] = useState<number>()
  const [annotateModal, showAnnotateModal] = useState(false)
  const [imageDetailsModal, showImageDetails] = useState(false)
  const [isViewOnly, setIsViewOnly] = useState(false)
  const [viewMode, setViewMode] = useState<ObservationsBrowserMode>(mode)
  const [tableSorting, setTableSorting] = useState<Sorting>({})

  const handleTableSort = (field: string, dir: SortingDirection) => {
    let newSorting: Sorting
    if (dir === undefined) {
      const { [field]: _, ...rest } = tableSorting
      newSorting = rest
    } else {
      newSorting = { ...tableSorting, [field]: dir }
    }
    setTableSorting(newSorting)
    const override = serializeSorting(newSorting) || undefined
    setParams((p) => ({ ...p, orderingOverride: override }))
  }

  const {
    data: observations,
    hasNextPage,
    fetchNextPage,
    isLoading: isLoadingObservations,
    isFetchingNextPage,
  } = useObservations(params)

  const { mutate: deleteObservation } = useObservationsDelete(
    onObservationsCreated
  )
  const { prependObservation } = useObservationsPrepend()
  const { updateObservation } = useObservationsUpdate()
  const {
    data: currentObservation,
    isLoading: isLoadingObservation,
  } = useObservation(currentObservationId)

  const annotationParams = useMemo(
    () => ({
      observationId: currentObservationId,
      trainingId: defaultFilters?.trainingId,
    }),
    [currentObservationId, defaultFilters?.trainingId]
  )

  const nextView = () => {
    const viewToggle = getNextMode(viewMode)
    setViewMode(viewToggle)
  }
  const openDetailsModal = (observation: Observation) => {
    setCurrentObservationId(observation.id)
    showImageDetails(true)
  }

  const openAnnotationModal = (observation: Observation) => {
    overrideLabelStudioSettings(productType)
    setIsViewOnly(false)
    setCurrentObservationId(observation.id)
    showAnnotateModal(true)
  }

  const openViewModal = (observation: Observation) => {
    overrideLabelStudioSettings(productType)
    setIsViewOnly(true)
    setCurrentObservationId(observation.id)
    showAnnotateModal(true)
  }

  const updateVisibleObservations = () => {
    const visibleObservations: Observation[] = []
    if (observations) {
      observations.map((obs, key) => visibleObservations.push(obs))
    }
    setVisibleObservations?.(visibleObservations)
  }

  const closeAnnotationModal = () => {
    showAnnotateModal(false)
    setCurrentObservationId(undefined)
    updateVisibleObservations()
    updateSelectedObservations?.(observations || [])
  }

  const triggerForNewRegion = () => {
    // In case of 'save' (the user click on SAVE button) and not skip to another observation,
    // following workaround deselect region to allow to automatically recreate a new region
    // on next annotation
    const annotationType = getAnnotationTypeBy(productType)
    if (annotationType === 'Brush') {
      const BRUSH_TOOL_BUTTON_CLASSNAME =
        'lsf-button lsf-button_type_dashed lsf-button_look_'
      const buttons: HTMLCollectionOf<HTMLButtonElement> = document.getElementsByClassName(
        BRUSH_TOOL_BUTTON_CLASSNAME
      ) as HTMLCollectionOf<HTMLButtonElement>
      if (buttons && buttons.length > 0) {
        const button: HTMLButtonElement = buttons[0]
        if (button) {
          button.click()
        }
      }
    }
  }

  const handleAnnotationCreated = (observation: Observation) => {
    toast.success(t('The annotation has been successfully saved.'))
    triggerForNewRegion()
    updateObservation(observation)
  }

  const defaultMenuContextualItems: MenuContextualItems = ({
    observation,
    setDisabled,
  }) => [
    {
      label: t('Image details'),
      icon: <FontAwesomeIcon icon={faCircleInfo} />,
      type: 'default',
      hideLabel: true,
      onClick: () => openDetailsModal(observation),
    },
    {
      label: t('View'),
      icon: <FontAwesomeIcon icon={faEye} />,
      type: 'default',
      hideLabel: true,
      onClick: () => openViewModal(observation),
    },
    {
      label: t('Annotate'),
      icon: <FontAwesomeIcon icon={faPaintbrush} />,
      type: 'default',
      hideLabel: true,
      onClick: () => openAnnotationModal(observation),
    },
    {
      label: t('Delete'),
      icon: <FontAwesomeIcon icon={faTrash} />,
      type: 'destroy',
      hideLabel: true,
      onClick: () => {
        setDisabled(true)
        deleteObservation({
          observationIds: [observation.id],
          productId,
        })
      },
    },
  ]

  const defaultOnItemClick: OnActionCallback = ({ observation }) =>
    openAnnotationModal(observation)

  const defaultTagDecorator = getTagDecorator(productType)

  const renderItem = (observation: Observation) => {
    const helpers = {
      openDetailsModal: () => openDetailsModal(observation),
      openAnnotationModal: () => openAnnotationModal(observation),
    }

    const itemRenderer = getItemRenderer(
      viewMode,
      renderTabularItem,
      renderGridItem
    )

    return itemRenderer(observation, helpers)
  }

  const itemRenderer =
    (isGridMode(mode) && !renderGridItem) ||
    (isTabularMode(mode) && !renderTabularItem)
      ? undefined // fallback to default implementation for renderers
      : renderItem

  const View = getViewComponent(viewMode)

  useEffect(() => {
    async function changeMode() {
      if (onModeChange) {
        onModeChange(viewMode)
      }
    }
    changeMode()
  }, [viewMode, onModeChange])

  useEffect(
    () => {
      setTableSorting({})
      setParams((p) => ({
        ...p,
        ...getLightweightParams(viewMode, imageData),
        orderingOverride: undefined,
      }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewMode, productId]
  )

  function backgroundResize() {
    const parentContainer = document.getElementById('asideDivId')?.parentElement
    if (parentContainer) {
      // convert document.documentElement.scrollHeight pixel to vh
      const newHeight =
        (document.documentElement.scrollHeight / window.innerHeight) * 100
      parentContainer.style.height = `${newHeight}vh`
    }
  }

  useEffect(() => {
    backgroundResize()
  }, [viewMode, observations?.length])

  const { alertNotifications } = useStore()
  const dispatch = useDispatch()

  useEffect(() => {
    const effectStartTime = Date.now()
    console.log(LOG_PREFIX, 'Upload notification useEffect mounted', {
      productId,
      alertNotificationsCount: alertNotifications?.length || 0,
      timestamp: new Date().toISOString(),
    })

    const updateProgress = (
      uploadId: string,
      status: 'UPDATING_UI' | 'COMPLETED_UPDATING_UI',
      progress: number
    ) => {
      console.log(LOG_PREFIX, 'updateProgress called', {
        uploadId,
        status,
        progress,
        timestamp: new Date().toISOString(),
        elapsedMs: Date.now() - effectStartTime,
      })
      showAlertNotification(dispatch, {
        eventType: 'IMAGE_UPLOAD_UPDATE',
        uploadId,
        status,
        progress,
      })
    }

    const handleAlertNotification = async (
      alertNotification: AlertNotificationImageUpload
    ) => {
      const handlerStartTime = Date.now()
      console.log(LOG_PREFIX, 'handleAlertNotification called', {
        status: alertNotification.status,
        uploadId: alertNotification.uploadId,
        observationsCount: alertNotification.observationsId?.length || 0,
        timestamp: new Date().toISOString(),
      })

      if (alertNotification.status !== 'COMPLETED') {
        console.log(LOG_PREFIX, 'Skipping - status is not COMPLETED', {
          status: alertNotification.status,
        })
        return
      }

      let progress = 0.001
      const { uploadId, observationsId } = alertNotification
      updateProgress(uploadId, 'UPDATING_UI', progress)

      const observationsLength = observationsId?.length || 0
      if (!observationsId || observationsLength === 0) {
        console.warn(LOG_PREFIX, 'No observations returned from backend', {
          uploadId,
          observationsId,
        })
        return
      }

      console.log(LOG_PREFIX, 'Processing COMPLETED notification', {
        uploadId,
        observationsLength,
        observationsId: observationsId.slice(0, 5), // Log first 5 IDs
        timestamp: new Date().toISOString(),
      })

      const fetchObservations = async (start: number, end: number) => {
        const chunkStartTime = Date.now()
        console.log(LOG_PREFIX, 'fetchObservations chunk starting', {
          uploadId,
          start,
          end,
          timestamp: new Date().toISOString(),
        })
        try {
          const queryString = getQueryString({
            ...getObservationsQueryParams(params),
            limit: end - start,
            offset: start,
          })
          const obs = await get<ObservationsResult>(
            `/observations/?${queryString}`
          )
          console.log(LOG_PREFIX, 'fetchObservations chunk completed', {
            uploadId,
            start,
            end,
            resultCount: obs.results.length,
            elapsedMs: Date.now() - chunkStartTime,
            timestamp: new Date().toISOString(),
          })
          obs.results.reverse().forEach((observation, index) => {
            prependObservation(observation)
            const newProgress = (index + 1) / observationsLength
            if (newProgress - progress > 0.1) {
              progress = newProgress
              updateProgress(uploadId, 'UPDATING_UI', progress)
            }
          })
        } catch (chunkError) {
          console.error(LOG_PREFIX, 'fetchObservations chunk FAILED', {
            uploadId,
            start,
            end,
            error: chunkError,
            errorMessage: (chunkError as Error)?.message,
            elapsedMs: Date.now() - chunkStartTime,
            timestamp: new Date().toISOString(),
          })
          throw chunkError // Re-throw to be caught by Promise.all
        }
      }

      const observationChunks = []
      for (let i = 0; i < observationsId.length; i += 20) {
        observationChunks.push({
          start: i,
          end: Math.min(i + 20, observationsId.length),
        })
      }

      console.log(LOG_PREFIX, 'Starting Promise.all for observation chunks', {
        uploadId,
        chunksCount: observationChunks.length,
        chunks: observationChunks,
        timestamp: new Date().toISOString(),
      })

      const promiseAllStartTime = Date.now()
      try {
        await Promise.all(
          observationChunks.map((chunk) =>
            fetchObservations(chunk.start, chunk.end)
          )
        )
        console.log(LOG_PREFIX, 'Promise.all completed successfully', {
          uploadId,
          chunksCount: observationChunks.length,
          elapsedMs: Date.now() - promiseAllStartTime,
          timestamp: new Date().toISOString(),
        })
      } catch (promiseAllError) {
        console.error(LOG_PREFIX, 'Promise.all FAILED', {
          uploadId,
          chunksCount: observationChunks.length,
          error: promiseAllError,
          errorMessage: (promiseAllError as Error)?.message,
          elapsedMs: Date.now() - promiseAllStartTime,
          timestamp: new Date().toISOString(),
        })
        // Note: continuing execution to still call COMPLETED_UPDATING_UI
        // so the toast gets dismissed even on partial failure
      }

      console.log(LOG_PREFIX, 'About to call COMPLETED_UPDATING_UI', {
        uploadId,
        handlerElapsedMs: Date.now() - handlerStartTime,
        timestamp: new Date().toISOString(),
      })

      updateProgress(uploadId, 'COMPLETED_UPDATING_UI', 0.999)
      markUploadCompleted(uploadId)
      onObservationsCreated?.(observationsId.length)
      window.dispatchEvent(new CustomEvent('observations-changed'))

      console.log(
        LOG_PREFIX,
        'UI update completed, checking for dataset association',
        { uploadId, timestamp: new Date().toISOString() }
      )

      // Handle dataset association if datasetId(s) is present
      // Retrieve datasetId(s) from localStorage
      const uploadDatasetMap = JSON.parse(
        localStorage.getItem('uploadDatasetMap') || '{}'
      )
      const datasetIdOrIds = uploadDatasetMap[uploadId]

      console.log(LOG_PREFIX, 'Retrieved dataset info from localStorage', {
        uploadId,
        datasetIdOrIds,
        hasDataset: !!datasetIdOrIds,
        allUploadMapKeys: Object.keys(uploadDatasetMap),
        timestamp: new Date().toISOString(),
      })

      if (datasetIdOrIds && observationsId.length > 0) {
        const datasetProcessingStartTime = Date.now()
        try {
          // Support both single dataset ID (string) and multiple dataset IDs (array)
          const datasetIds = Array.isArray(datasetIdOrIds)
            ? datasetIdOrIds
            : [datasetIdOrIds]

          console.log(LOG_PREFIX, '>>> PHASE START: Dataset processing', {
            uploadId,
            datasetIds,
            datasetCount: datasetIds.length,
            observationsCount: observationsId.length,
            timestamp: new Date().toISOString(),
          })

          // Process each dataset
          // eslint-disable-next-line no-plusplus
          for (let i = 0; i < datasetIds.length; i++) {
            const datasetId = datasetIds[i]

            // Check if this is a new dataset (prefixed with 'new:')
            if (datasetId.startsWith('new:')) {
              const newDatasetName = datasetId.substring(4) // Remove 'new:' prefix

              console.log(
                LOG_PREFIX,
                `>>> DATASET CREATION START: "${newDatasetName}"`,
                {
                  uploadId,
                  newDatasetName,
                  productId,
                  observationsCount: observationsId.length,
                  datasetIndex: i + 1,
                  totalDatasets: datasetIds.length,
                  timestamp: new Date().toISOString(),
                }
              )

              // Create new dataset with observations
              // eslint-disable-next-line no-await-in-loop
              const createResponse = await post('/dataset/', {
                name: newDatasetName,
                product_id: productId,
                observations_ids: observationsId,
              })

              console.log(
                LOG_PREFIX,
                `>>> DATASET CREATION END: "${newDatasetName}" - SUCCESS`,
                {
                  uploadId,
                  newDatasetName,
                  response: createResponse,
                  timestamp: new Date().toISOString(),
                }
              )

              toast.success(t('Dataset successfully created'))
            } else {
              console.log(
                LOG_PREFIX,
                `>>> DATASET UPDATE START: Adding to existing dataset ${datasetId}`,
                {
                  uploadId,
                  datasetId,
                  newObservationsCount: observationsId.length,
                  datasetIndex: i + 1,
                  totalDatasets: datasetIds.length,
                  timestamp: new Date().toISOString(),
                }
              )

              // Existing dataset - add observations to it
              // Get all current observations in the dataset by querying observations endpoint
              // eslint-disable-next-line no-await-in-loop
              const datasetObservations = await get<ObservationsResult>(
                `/observations/?dataset=${datasetId}&limit=10000`
              )

              // Extract existing observation IDs
              const existingObservationIds = datasetObservations.results.map(
                (obs) => obs.id
              )

              console.log(LOG_PREFIX, 'Fetched existing observations', {
                uploadId,
                datasetId,
                existingCount: existingObservationIds.length,
                timestamp: new Date().toISOString(),
              })

              // Get dataset info for name
              // eslint-disable-next-line no-await-in-loop
              const currentDataset = await get<{
                id: number
                name: string
              }>(`/dataset/${datasetId}/`)

              // Merge existing observations with new ones (avoiding duplicates)
              const updatedObservationIds = [
                ...new Set([...existingObservationIds, ...observationsId]),
              ]

              console.log(
                LOG_PREFIX,
                `Updating dataset "${currentDataset.name}" with merged observations`,
                {
                  uploadId,
                  datasetId,
                  datasetName: currentDataset.name,
                  previousCount: existingObservationIds.length,
                  addingCount: observationsId.length,
                  newTotal: updatedObservationIds.length,
                  timestamp: new Date().toISOString(),
                }
              )

              // Update the dataset with the new observation IDs
              // eslint-disable-next-line no-await-in-loop
              await update(`/dataset/${datasetId}/`, {
                name: currentDataset.name,
                product_id: productId,
                observations_ids: updatedObservationIds,
              })

              console.log(
                LOG_PREFIX,
                `>>> DATASET UPDATE END: "${currentDataset.name}" - SUCCESS`,
                {
                  uploadId,
                  datasetId,
                  datasetName: currentDataset.name,
                  finalObservationsCount: updatedObservationIds.length,
                  timestamp: new Date().toISOString(),
                }
              )

              toast.success(t('Images successfully added to dataset'))
            }
          }

          console.log(LOG_PREFIX, '>>> PHASE END: Dataset processing', {
            uploadId,
            datasetCount: datasetIds.length,
            elapsedMs: Date.now() - datasetProcessingStartTime,
            timestamp: new Date().toISOString(),
          })

          // Emit event to notify other components that observations were added to dataset
          console.log(
            LOG_PREFIX,
            'Emitting observations-added-to-dataset event',
            { uploadId, timestamp: new Date().toISOString() }
          )
          window.dispatchEvent(new CustomEvent('observations-added-to-dataset'))

          // Clean up the localStorage entry
          delete uploadDatasetMap[uploadId]
          localStorage.setItem(
            'uploadDatasetMap',
            JSON.stringify(uploadDatasetMap)
          )

          console.log(LOG_PREFIX, 'Cleaned up localStorage entry', {
            uploadId,
            remainingKeys: Object.keys(uploadDatasetMap),
            timestamp: new Date().toISOString(),
          })
        } catch (err) {
          console.error(LOG_PREFIX, '>>> DATASET PROCESSING ERROR', {
            uploadId,
            datasetIdOrIds,
            error: err,
            errorMessage: (err as Error)?.message,
            errorResponse: (err as AxiosError)?.response?.data,
            elapsedMs: Date.now() - datasetProcessingStartTime,
            timestamp: new Date().toISOString(),
          })

          // Clean up localStorage even on error to prevent stale data
          try {
            const cleanupMap = JSON.parse(
              localStorage.getItem('uploadDatasetMap') || '{}'
            )
            delete cleanupMap[uploadId]
            localStorage.setItem('uploadDatasetMap', JSON.stringify(cleanupMap))
            console.log(LOG_PREFIX, 'Cleaned up localStorage after error', {
              uploadId,
            })
          } catch (cleanupErr) {
            console.error(LOG_PREFIX, 'Failed to clean up localStorage', {
              cleanupErr,
            })
          }

          handleApiErrors(err as AxiosError)

          // Show specific error message for dataset creation failure
          const axiosError = err as AxiosError<{
            detail?: string
            name?: string[]
          }>
          if (axiosError?.response?.data) {
            const errorData = axiosError.response.data
            if (errorData.name && Array.isArray(errorData.name)) {
              // Dataset name validation error (e.g., duplicate name)
              toast.error(`${t('Error')}: ${errorData.name.join(', ')}`)
            } else if (errorData.detail) {
              toast.error(`${t('Error')}: ${errorData.detail}`)
            }
          }
        }
      } else {
        console.log(LOG_PREFIX, 'No dataset association needed', {
          uploadId,
          datasetIdOrIds,
          observationsCount: observationsId.length,
        })
      }

      // Track chunk completion for chunked uploads with datasets
      // Check if this uploadId is a chunk (contains '_chunk_')
      if (uploadId.includes('_chunk_')) {
        const parentUploadId = uploadId.split('_chunk_')[0]
        const chunkIndex = parseInt(uploadId.split('_chunk_')[1], 10)

        console.log(LOG_PREFIX, 'Chunk completion detected', {
          uploadId,
          parentUploadId,
          chunkIndex,
          timestamp: new Date().toISOString(),
        })

        // Update chunk tracker
        const chunkTracker = JSON.parse(
          localStorage.getItem('chunkTracker') || '{}'
        )

        if (chunkTracker[parentUploadId]) {
          // Add this chunk to completed list if not already there
          if (
            !chunkTracker[parentUploadId].completedChunks.includes(chunkIndex)
          ) {
            chunkTracker[parentUploadId].completedChunks.push(chunkIndex)
            localStorage.setItem('chunkTracker', JSON.stringify(chunkTracker))

            const { totalChunks, completedChunks } = chunkTracker[
              parentUploadId
            ]

            console.log(LOG_PREFIX, 'Updated chunk tracker', {
              parentUploadId,
              chunkIndex,
              completedChunks: completedChunks.length,
              totalChunks,
              allCompleted: completedChunks.length >= totalChunks,
              timestamp: new Date().toISOString(),
            })

            // Check if all chunks are completed
            if (completedChunks.length >= totalChunks) {
              console.log(
                LOG_PREFIX,
                'All chunks completed - closing parent toast',
                {
                  parentUploadId,
                  totalChunks,
                  completedChunks: completedChunks.length,
                  timestamp: new Date().toISOString(),
                }
              )

              // Clean up chunk tracker
              delete chunkTracker[parentUploadId]
              localStorage.setItem('chunkTracker', JSON.stringify(chunkTracker))

              // Close the parent toast
              updateProgress(parentUploadId, 'COMPLETED_UPDATING_UI', 1)
            }
          }
        } else {
          console.log(LOG_PREFIX, 'No chunk tracker found for parent', {
            parentUploadId,
            availableTrackers: Object.keys(chunkTracker),
          })
        }
      }
    }

    if (alertNotifications && alertNotifications.length > 0) {
      console.log(LOG_PREFIX, 'Processing alertNotifications from store', {
        count: alertNotifications.length,
        notifications: alertNotifications.map((an) => ({
          uploadId: an.uploadId,
          status: an.status,
          observationsCount: an.observationsId?.length,
        })),
        timestamp: new Date().toISOString(),
      })

      alertNotifications.forEach((alertNotification) => {
        console.log(
          LOG_PREFIX,
          'Invoking handleAlertNotification for notification',
          {
            uploadId: alertNotification.uploadId,
            status: alertNotification.status,
            timestamp: new Date().toISOString(),
          }
        )
        handleAlertNotification(alertNotification).catch((err) => {
          console.error(
            LOG_PREFIX,
            'handleAlertNotification promise rejected (unhandled)',
            {
              uploadId: alertNotification.uploadId,
              error: err,
              errorMessage: (err as Error)?.message,
              timestamp: new Date().toISOString(),
            }
          )
        })
      })
    } else {
      console.log(LOG_PREFIX, 'No alertNotifications to process', {
        alertNotifications,
        timestamp: new Date().toISOString(),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertNotifications])

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={classes.container}>
      <ObservationsBrowserTop
        uploadComponent={
          !showUploadObservationsButton ? undefined : (
            <UploadObservations
              productId={productId}
              productType={productType}
            />
          )
        }
        uploadDisabled={uploadDisabled}
        leftContent={subHeaderContent}
      >
        <div>
          {widgets && widgets({ params, setParams, viewMode })}
          <Divider show={!!widgets} />
          <SwitchViewButtons value={viewMode} onchange={nextView} />
          <Divider
            show={isLoadingObservation}
            marginLeft=".2rem"
            marginRight="1rem"
          />
          {isLoadingObservation && (
            <>
              {t('Loading')} <Loading />
            </>
          )}
        </div>
      </ObservationsBrowserTop>

      <InfiniteListWrapper>
        <InfiniteScroll
          onScroll={() => {
            backgroundResize()
          }}
          className={classes.avoidUserSelect}
          loadMore={() => fetchNextPage()}
          hasMore={hasNextPage}
          useWindow={useWindowScroll}
          getScrollParent={
            useWindowScroll
              ? undefined
              : () =>
                  scrollContainerId
                    ? document.getElementById(scrollContainerId) || null
                    : null
          }
        >
          {useEffect(() => {
            if (hasNextPage) {
              backgroundResize()
            }
            updateVisibleObservations()
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [observations?.length])}
          <div id="divImagesId" className={classes.container}>
            <View
              observations={observations}
              tagDecorator={tagDecorator || defaultTagDecorator}
              menuContextualItems={
                menuContextualItems || defaultMenuContextualItems
              }
              onItemClick={onItemClick || defaultOnItemClick}
              emptyMessage={t(
                "There's nothing here. Try uploading a new image."
              )}
              tableSorting={tableSorting}
              onColumnSort={handleTableSort}
            >
              {itemRenderer}
            </View>
          </div>
          {(isLoadingObservations || isFetchingNextPage) && (
            <LoadingBlock message={t('Loading')} key="loading" />
          )}
        </InfiniteScroll>
      </InfiniteListWrapper>

      {annotateModal && (
        <Annotator
          productId={productId}
          params={annotationParams}
          defaultFilters={{
            ...params,
            ...(trainingId && { availableForTesting: Number(trainingId) }),
          }}
          annotationType={getAnnotationTypeBy(productType)}
          onAnnotationCreated={handleAnnotationCreated}
          withLightbox
          onCloseRequest={closeAnnotationModal}
          mode={viewingMode || isViewOnly ? 'viewing' : 'editing'}
          selectedObservationId={currentObservationId}
        />
      )}

      {imageDetailsModal && currentObservation && (
        <ImageLightbox
          closeButtonLabel={t('Close image')}
          onCloseRequest={() => showImageDetails(false)}
          alt={currentObservation.filename}
          src={currentObservation.large}
          noFitImage
        />
      )}
    </div>
  )
}

export default ObservationsBrowser
